import { Injectable } from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {getCookie} from "../helpers/utils";
import {type} from "os";

@Injectable({
  providedIn: 'root'
})
export class RatingEditorService {

  private _authorized: boolean = false;
  private _apiPrefix: string = '/api/v1/';
  private _ratingsAPIPrefix: string = `${this._apiPrefix}ratings/`;
  private _ratingStatusesURL: string = `${this._apiPrefix}rating_status/`;
  private _gamesListURL: string = `${this._apiPrefix}rating_games/`;
  private _tournamentsListUrl: string = `${this._apiPrefix}get_tournaments/list_mini/`;
  private _ratingIdToModify: string | number;
  private _ratingBallsTable: RatingBallsRow[] = [];
  private _brandNewRating: boolean = false;
  private _statusesForCountries = [
    2,
    3,
    4,
    5,
    6,
  ];

  private _statusesForCity = [
    5,
    6
  ];
  private _ratingPointsUrl =
    (ratingId: string | number) => `${this._ratingsAPIPrefix}${ratingId}/rating_points/`;

  private _ratingDescriptiveDataUrl =
    (ratingId: string | number) => `${this._ratingsAPIPrefix}${ratingId}/`;

  private _saveRatingPointsUrl =
    (ratingId: string | number) => `${this._ratingsAPIPrefix}${ratingId}/change_rating_points/`;
  private _createRatingPointsUrl = `${this._ratingsAPIPrefix}change_rating_points/`;

  private _ratingStatusesList: RatingStatus[] = [];
  private _gamesList: GameType[] = [];
  private _selectedStatus: RatingStatus;
  private _selectedGameType: GameType;
  private _allDescriptiveData: DescriptiveDataBackEnd;
  private _someFieldWasTouched = false;
  public typedAndSelectedValues = {
    ratingName: '',
    ratingStatus: null,
    ratingCountry: null,
    ratingCity: null,
    ratingGameType: null,
    ratingTournamentsIdsList: [],
    ratingDateFrom: '',
    ratingDateTo: '',
  };

  private _transferRatingBallsToRawForm = (): RatingBallsRowBackEnd[] => {
    return this._ratingBallsTable.map((row, index) => {

      const baseObjectToSave: RatingBallsRowBackEnd = {
        place_from: row.from,
        place_to: row.to,
        point_count: row.balls,
      };


      if (this.brandNewRating || typeof row?.pointId === 'number') {
        baseObjectToSave.point_id = row?.pointId;
      }

      return baseObjectToSave;

    });
  }

  private _transferDescriptiveDataToRawForm = (): DescriptiveDataToSave => {
    const currentDate = new Date().toISOString().split('T')[0]
    return {
      title: this.typedAndSelectedValues.ratingName,
      status: this.typedAndSelectedValues.ratingStatus,
      country: this.typedAndSelectedValues.ratingCountry,
      city: this.typedAndSelectedValues.ratingCity,
      game: this.typedAndSelectedValues.ratingGameType,
      tournaments: [...this.typedAndSelectedValues.ratingTournamentsIdsList],
    }
  }

  constructor(
    private http: HttpClient,
  ) { }

  private getCsrfToken(): string {
    return getCookie('csrftoken');
  }

  getRatingsStatusesList() {
    return this.http.get(this._ratingStatusesURL);
  }

  getGamesList() {
    return this.http.get(this._gamesListURL);
  }

  getRatingPointsTable() {
    return this.http.get(this._ratingPointsUrl(this.ratingIdToModify));
  }

  getRatingDescriptiveData() {
    return this.http.get(this._ratingDescriptiveDataUrl(this.ratingIdToModify));
  }

  getFilteredTournamentsList(tournamentTitle: string) {
    return this.http.get(`${this._tournamentsListUrl}?search=${tournamentTitle}`);
  }

  createNewRating() {
    return this.http.post(
      this._ratingsAPIPrefix,
      this._transferDescriptiveDataToRawForm(),
      {
      headers: {
        'X-CSRFToken': this.getCsrfToken()
      }
    })
  }

  updateRating() {
    return this.http.put(
      this._ratingDescriptiveDataUrl(this.ratingIdToModify),
      this._transferDescriptiveDataToRawForm(),
      {
        headers: {
          'X-CSRFToken': this.getCsrfToken()
        }
      })
  }

  saveRatingPoints() {
    console.debug('this is saveRatingPoints CALL!!');
    return this.http.put(
      this._saveRatingPointsUrl(this.ratingIdToModify),
      {
        points: this._transferRatingBallsToRawForm(),
      },
      {
        headers: {
          'X-CSRFToken': this.getCsrfToken()
        }
      })
  }

  createRatingPoints() {
    return this.http.put(
      this._createRatingPointsUrl,
      {
        points: this._transferRatingBallsToRawForm(),
      },
      {
        headers: {
          'X-CSRFToken': this.getCsrfToken()
        }
      })
  }

  get authorized() {
    return this._authorized;
  }

  set authorized(value) {
    this._authorized = value;
  }

  get ratingStatusesList() {
    return this._ratingStatusesList;
  }
  set ratingStatusesList(value) {
    this._ratingStatusesList = value;
  }

  get gamesList() {
    return this._gamesList;
  }
  set gamesList(value) {
    this._gamesList = value;
  }
  get selectedStatus() {
    return this._selectedStatus;
  };
  set selectedStatus(value) {
    this._selectedStatus = value;
  };

  get selectedGameType() {
    return this._selectedGameType;
  };

  set selectedGameType(value) {
    this._selectedGameType = value;
  }

  get ratingIdToModify() {
    return this._ratingIdToModify;
  }

  set ratingIdToModify(value) {
    this._ratingIdToModify = value;
  }

  get ratingBallsTable(): RatingBallsRow[] {
    return this._ratingBallsTable;
  }

  set ratingBallsTable(table) {
    this._ratingBallsTable = table;
  }

  get allDescriptiveData(): DescriptiveDataBackEnd {
    return this._allDescriptiveData;
  }

  set allDescriptiveData(value) {
    this._allDescriptiveData = value;
  }

  get brandNewRating(): boolean {
    return this._brandNewRating;
  }

  set brandNewRating(value: boolean) {
    this._brandNewRating = value;
  }

  noRatingNameSpecified() {
    return !this.typedAndSelectedValues.ratingName;
  }

  noRatingStatusSpecified() {
    return typeof this.typedAndSelectedValues.ratingStatus !== 'number';
  }

  noRatingCountrySpecified() {
    return !this.ratingCountryDisabled() && typeof this.typedAndSelectedValues.ratingCountry !== 'number';
  }

  ratingCityDisabled() {
    return !this._statusesForCity.includes(this.typedAndSelectedValues.ratingStatus);
  }

  ratingCountryDisabled() {
    return !this._statusesForCountries.includes(this.typedAndSelectedValues.ratingStatus);
  }

  noRatingCitySpecified() {
    return !this.ratingCityDisabled()
      && typeof this.typedAndSelectedValues.ratingCity !== 'number';
  }

  noRatingGameTypeSpecified() {
    return typeof this.typedAndSelectedValues.ratingGameType !== 'number';
  }

  saveOrCreateIsEnabled(): boolean {
    const someValuesAreEmpty
      = this.noRatingNameSpecified()
      || this.noRatingStatusSpecified()
      || this.noRatingGameTypeSpecified()
      || this.noRatingCitySpecified()
      || this.noRatingCountrySpecified();

    return !someValuesAreEmpty && this.someFieldWasTouched;
  }

  saveRating() {

  }

  get someFieldWasTouched(): boolean {
    return this._someFieldWasTouched;
  }

  set someFieldWasTouched(value: boolean) {
    this._someFieldWasTouched = value;
  }

  get statusesForCountries(): number[] {
    return this._statusesForCountries;
  }

  set statusesForCountries(list) {
    this._statusesForCountries = list;
  }

  get statusesForCity(): number[] {
    return this._statusesForCity;
  }

  set statusesForCity(list: number[]) {
    this._statusesForCity = list;
  }

  initiateDefaultRatingBallsTable() {
    this._ratingBallsTable = [
      {
        from: 1,
        to: 1,
        balls: 300,
      },
      {
        from: 2,
        to: 2,
        balls: 280,
      },
      {
        from: 3,
        to: 4,
        balls: 260,
      },
      {
        from: 5,
        to: 8,
        balls: 240,
      },
      {
        from: 9,
        to: 12,
        balls: 220,
      },
      {
        from: 13,
        to: 16,
        balls: 200,
      },
      {
        from: 17,
        to: 24,
        balls: 180,
      },
      {
        from: 25,
        to: 32,
        balls: 160,
      },
      {
        from: 33,
        to: 48,
        balls: 140,
      },
      {
        from: 49,
        to: 64,
        balls: 120,
      },
    ];
  }
}

export interface GameType {
  id: number,
  title: string,
}

export interface RatingStatus {
  id: number,
  title: string,
  need_city?: boolean,
  need_country?: boolean,
}

export interface RatingBallsRow {
  from: number,
  to: number,
  balls: number,
  pointId?: number,
}

export interface RatingBallsRowBackEnd {
  point_id?: number,
  place_from: number,
  place_to: number,
  point_count: number,
}

interface TournamentDataBackend {
  id: number,
  name: string,
}
export interface DescriptiveDataBackEnd {
  rating_id: number,
  city: number,
  city_data: {
    cityId: number,
    cityName: string
  },
  country: number,
  country_data: {
    countryId: number,
    countryName: string,
  },
  created_datetime: string,
  date_from: string,
  date_to: string,
  detail_url: string,
  game: number,
  game_data: {
    gameId: number,
    gameName: string,
  },
  status: number,
  status_data: {
    statusId: number,
    statusName: string,
  },
  title: string,
  tournament_players_count: number,
  tournaments: TournamentDataBackend[];
  tournaments_count: number,
}

export interface DescriptiveDataToSave {
  title: string,
  status: number,
  country: number,
  city: number,
  game: number,
  tournaments: number[],
}
